import { createContext, useContext, useState } from 'react';
import EverTrue from 'app';
import PropTypes from 'prop-types';
import Api from 'entities/helpers/api';
import { isEmpty, debounce } from '@evertrue/sdk';
import { getQueryTemplate } from './config/global-search-config';
import { useGate } from 'components/is-gated';

export const GlobalSearchContext = createContext({});

const SEARCH_LIMIT = 10;

export const GlobalSearchProvider = ({ children }) => {
  const hasInteractions = useGate('ugc_show_imported_notes');

  const [contactResults, setContactResults] = useState([]);
  const [interactionResults, setInteractionResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [queryType, setQueryType] = useState('all'); // ["all", "name", "phone", "email", "interaction", "remoteId"]

  const handleDebouncedSearch = debounce(async (searchQuery, type) => {
    setLoading(true);
    // Always update the query state immediately when search is triggered
    setQuery(searchQuery);

    if (isEmpty(searchQuery)) {
      setContactResults([]);
      setInteractionResults([]);
      setLoading(false);
      return;
    }
    if ((type === 'interaction' || type === 'all') && hasInteractions) {
      try {
        const response = await Api.SEARCH.CONTACT_NOTE.post({
          data: JSON.stringify(getQueryTemplate(searchQuery, 'interaction')),
          params: { limit: SEARCH_LIMIT },
        });
        setInteractionResults(response.items);
      } catch (e) {
        EverTrue.Alert.error('Error fetching interactions:', e);
      }
    }
    if (type !== 'interaction') {
      try {
        const response = await Api.CONTACTS.SEARCH.post({
          data: JSON.stringify(getQueryTemplate(searchQuery, type)),
          params: { limit: SEARCH_LIMIT },
        });
        setContactResults(response.items);
      } catch (e) {
        EverTrue.Alert.error('Error fetching contacts:', e);
      }
    }
    setLoading(false);
  }, 350);

  const clearSearch = () => {
    setQuery('');
    setContactResults([]);
    setInteractionResults([]);
  };

  return (
    <GlobalSearchContext.Provider
      value={{
        contactResults,
        interactionResults,
        loading,
        query,
        setQuery,
        queryType,
        setQueryType,
        handleDebouncedSearch,
        clearSearch,
      }}
    >
      {children}
    </GlobalSearchContext.Provider>
  );
};

export const useGlobalSearch = () => {
  if (!GlobalSearchContext) {
    throw new Error('useGlobalSearch must be used within a GlobalSearchProvider');
  }
  return useContext(GlobalSearchContext);
};

GlobalSearchProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
